<script setup lang="ts">
import { computed, inject, type Ref } from 'vue'
import {
  getAdminCreditNoteFileUrl,
  getAdminInvoiceFileUrl,
  useGetAdminCompanyBilling,
  useListAdminCompanyCreditNotes,
  useListAdminCompanyInvoices,
} from '@swipe4work/api-orval'
import type {
  GetAdminCompanyBillingSubscriptionSnapshotDto,
  ListCreditNotesForCompanyCreditNoteDto,
  ListInvoicesForCompanyInvoiceDto,
} from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel, SubscriptionStatus } from '@swipe4work/models-orval'
import StatTile from '@/shared/components/analytics/StatTile.vue'
import type { MoveSubscriptionStartDateDialogData } from '@/features/companies/dialogs/MoveSubscriptionStartDateDialog.vue'
import MoveSubscriptionStartDateDialog from '@/features/companies/dialogs/MoveSubscriptionStartDateDialog.vue'

definePage({
  path: '/companies/:id/facturatie',
  name: 'companies.[id].facturatie',
  meta: {
    permission: {
      resource: 'CompanyInvoicing',
      level: 'Read',
    },
  },
})

const companyId = inject<Ref<number>>('companyId')!

const { data: billingData, isPending: billingPending, refetch: refetchBilling } = useGetAdminCompanyBilling(computed(() => companyId.value))
const { data: invoicesData, isPending: invoicesPending } = useListAdminCompanyInvoices(
  computed(() => companyId.value),
  { query: { staleTime: 5 * 60 * 1000 } },
)
const { data: creditNotesData, isPending: creditNotesPending } = useListAdminCompanyCreditNotes(
  computed(() => companyId.value),
  { query: { staleTime: 5 * 60 * 1000 } },
)

const billing = computed(() => billingData.value)
const metrics = computed(() => billing.value?.metrics)
const activeSubscription = computed(() => billing.value?.activeSubscription)
const pastSubscriptions = computed(() => billing.value?.pastSubscriptions ?? [])
const futureSubscriptions = computed(() => billing.value?.futureSubscriptions ?? [])

const invoices = computed<ListInvoicesForCompanyInvoiceDto[]>(() => invoicesData.value?.invoices ?? [])
const creditNotes = computed<ListCreditNotesForCompanyCreditNoteDto[]>(() => creditNotesData.value?.creditNotes ?? [])

const creditNoteByInvoiceId = computed(() => {
  const map = new Map<string, ListCreditNotesForCompanyCreditNoteDto>()

  for (const note of creditNotes.value) {
    if (note.invoiceId) {
      map.set(note.invoiceId, note)
    }
  }

  return map
})

function findCreditNote(invoiceId: string): ListCreditNotesForCompanyCreditNoteDto | undefined {
  return creditNoteByInvoiceId.value.get(invoiceId)
}

const historicalSubscriptions = computed(() => [ ...futureSubscriptions.value, ...pastSubscriptions.value ])

// The first not-yet-started subscription (backend puts StartsOn > today in futureSubscriptions).
const upcomingSubscription = computed(() => futureSubscriptions.value[0] ?? null)

const dialog = useDynamicDialog()

function openMoveStartDateDialog() {
  if (!upcomingSubscription.value?.startsOn) return

  const data: MoveSubscriptionStartDateDialogData = {
    target: 'company',
    id: companyId.value,
    currentStartDate: upcomingSubscription.value.startsOn,
  }

  dialog.open(MoveSubscriptionStartDateDialog, {
    props: {
      header: 'Ingangsdatum abonnement vervroegen',
      modal: true,
      style: {
        width: 'auto',
        height: 'auto', 
      },
    },
    data,
    onClose: () => refetchBilling(),
  })
}

const eurFormatter = new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: 'EUR',
})

const dateFormatter = new Intl.DateTimeFormat('nl-NL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—'

  return eurFormatter.format(value)
}

function formatDate(value: Date | string | null | undefined): string {
  if (value == null) return '—'
  const d = typeof value === 'string' ? new Date(value) : value
  if (Number.isNaN(d.getTime())) return '—'

  return dateFormatter.format(d)
}

const totalDealSizeLabel = computed(() => formatCurrency(metrics.value?.totalDealSizeExclVat))
const totalInvoicedLabel = computed(() => formatCurrency(metrics.value?.totalInvoicedExclVat))
const totalCreditedLabel = computed(() => formatCurrency(metrics.value?.totalCreditedExclVat))
const avgMonthlyLabel = computed(() => formatCurrency(metrics.value?.averageMonthlyValue))
const avgYearlyLabel = computed(() => formatCurrency(metrics.value?.averageYearlyValue))
const nextRenewalLabel = computed(() => formatDate(metrics.value?.nextRenewalDate))
const firstStartSubtitle = computed(() => {
  if (!metrics.value?.firstSubscriptionStart) return undefined

  return `sinds ${formatDate(metrics.value.firstSubscriptionStart)}`
})
const remainingSlotsLabel = computed(() => {
  if (metrics.value?.remainingJobSlots == null) return '—'

  return String(metrics.value.remainingJobSlots)
})
const remainingSlotsSubtitle = computed(() => {
  if (metrics.value?.usedJobSlots == null || metrics.value?.totalJobSlots == null) return undefined

  return `${metrics.value.usedJobSlots} / ${metrics.value.totalJobSlots} gebruikt`
})

function statusSeverity(status: SubscriptionStatus): 'success' | 'danger' | 'secondary' {
  if (status === SubscriptionStatus.active) return 'success'
  if (status === SubscriptionStatus.deactivated) return 'danger'

  return 'secondary'
}

function statusLabel(status: SubscriptionStatus): string {
  if (status === SubscriptionStatus.active) return 'Actief'
  if (status === SubscriptionStatus.deactivated) return 'Beëindigd'

  return status
}

function invoiceStatusSeverity(invoice: ListInvoicesForCompanyInvoiceDto): 'success' | 'warning' | 'danger' | 'info' {
  if (findCreditNote(invoice.id)) return 'info'
  if (invoice.status === 'matched') return 'success'
  if (invoice.status === 'outstanding' && invoice.daysOverdue === 0) return 'warning'

  return 'danger'
}

function invoiceStatusLabel(invoice: ListInvoicesForCompanyInvoiceDto): string {
  if (findCreditNote(invoice.id)) return 'Gecrediteerd'
  if (invoice.status === 'matched') return 'Betaald'
  if (invoice.status === 'outstanding' && invoice.daysOverdue === 0) return 'Openstaand'

  return `${invoice.daysOverdue} dagen te laat`
}

function openDownloadUrl(url: string) {
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

async function downloadInvoice(fileId: string) {
  try {
    const result = await getAdminInvoiceFileUrl(companyId.value, fileId)
    if (!result?.fileUrl) return
    openDownloadUrl(result.fileUrl)
  }
  catch (e) {
    console.error('Failed to fetch invoice file url', e)
  }
}

async function downloadCreditNote(creditNoteId: string) {
  try {
    const result = await getAdminCreditNoteFileUrl(companyId.value, creditNoteId)
    if (!result?.fileUrl) return
    openDownloadUrl(result.fileUrl)
  }
  catch (e) {
    console.error('Failed to fetch credit-note file url', e)
  }
}

function durationLabel(sub: GetAdminCompanyBillingSubscriptionSnapshotDto): string {
  return `${sub.billingPeriodMonths} mnd`
}
</script>

<template>
  <div class="relative space-y-5">
    <!-- KPI tiles -->
    <div class="grid grid-cols-2 gap-3 lg:grid-cols-6 md:grid-cols-3">
      <StatTile
        label="Total deal size (excl. BTW)"
        :value="totalDealSizeLabel"
        subtitle="Gefactureerd minus gecrediteerd"
        mono
      />
      <StatTile
        label="Totaal gefactureerd (excl. BTW)"
        :value="totalInvoicedLabel"
        mono
      />
      <StatTile
        label="Totaal gecrediteerd (excl. BTW)"
        :value="totalCreditedLabel"
        mono
      />
      <StatTile
        label="Gemiddelde waarde / maand"
        :value="avgMonthlyLabel"
        :subtitle="firstStartSubtitle"
        mono
      />
      <StatTile
        label="Gemiddelde waarde / jaar"
        :value="avgYearlyLabel"
        :subtitle="firstStartSubtitle"
        mono
      />
      <StatTile
        label="Volgende verlenging"
        :value="nextRenewalLabel"
        mono
      />
      <StatTile
        v-if="metrics?.totalJobSlots != null"
        label="Resterende vacature slots"
        :value="remainingSlotsLabel"
        :subtitle="remainingSlotsSubtitle"
        mono
      />
      <StatTile
        v-if="metrics?.overriddenJobSlots != null"
        label="Overschreven slot-limiet"
        :value="String(metrics.overriddenJobSlots)"
        mono
      />
    </div>

    <!-- Loading skeleton -->
    <div v-if="billingPending" class="space-y-3">
      <Skeleton height="3rem" />
      <Skeleton height="14rem" />
    </div>

    <!-- Not-yet-started subscription notice -->
    <Card v-if="!billingPending && upcomingSubscription">
      <template #content>
        <div class="flex items-start gap-3">
          <i class="fa-regular fa-clock mt-1 text-sm text-warning-600" />
          <div class="flex flex-1 flex-col gap-1">
            <div class="flex items-center gap-2">
              <h3 class="text-lg font-semibold">
                Abonnement nog niet gestart
              </h3>
              <Tag severity="warn" class="!text-xs !font-medium">
                Start {{ formatDate(upcomingSubscription.startsOn) }}
              </Tag>
            </div>
            <p class="text-sm text-surface-600">
              {{ upcomingSubscription.title }} — de eerste factuur wordt door Teamleader gegenereerd op de
              ingangsdatum ({{ formatDate(upcomingSubscription.startsOn) }}, geplande factuurdatum). De
              abonnementsperiode ({{ durationLabel(upcomingSubscription) }}) begint op die datum.
            </p>
          </div>
          <Button
            v-permission="{ resource: AdminPermissionResource.Company, level: PermissionLevel.Write }"
            label="Vervroegen"
            size="small"
            severity="secondary"
            @click="openMoveStartDateDialog"
          />
        </div>
      </template>
    </Card>

    <!-- Active subscription card -->
    <Card v-if="!billingPending && activeSubscription">
      <template #content>
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-lg font-semibold">
              Actief abonnement
            </h3>
            <Tag :severity="statusSeverity(activeSubscription.status)">
              {{ statusLabel(activeSubscription.status) }}
            </Tag>
          </div>
          <div class="text-sm text-surface-600">
            {{ activeSubscription.title }}
          </div>
          <div class="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              <div class="text-xs text-surface-500">
                Start
              </div>
              <div class="font-mono">
                {{ formatDate(activeSubscription.startsOn) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Einde
              </div>
              <div class="font-mono">
                {{ formatDate(activeSubscription.endsOn) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Volgende verlenging
              </div>
              <div class="font-mono">
                {{ formatDate(activeSubscription.nextRenewalDate) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Periode
              </div>
              <div class="font-mono">
                {{ durationLabel(activeSubscription) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Vacatureslots
              </div>
              <div class="font-mono">
                {{ activeSubscription.jobSlots ?? '—' }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Totaal excl. BTW
              </div>
              <div class="font-mono">
                {{ formatCurrency(activeSubscription.totalTaxExclusive) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Totaal incl. BTW
              </div>
              <div class="font-mono">
                {{ formatCurrency(activeSubscription.totalTaxInclusive) }}
              </div>
            </div>
            <div>
              <div class="text-xs text-surface-500">
                Prijs per maand
              </div>
              <div class="font-mono">
                {{ formatCurrency(activeSubscription.pricePerMonth) }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </Card>
    <Card v-else>
      <template #content>
        <div class="text-sm text-surface-500">
          Geen actief abonnement.
        </div>
      </template>
    </Card>

    <!-- Historical + future subscriptions -->
    <Card>
      <template #content>
        <div class="flex flex-col gap-3">
          <h3 class="text-lg font-semibold">
            Abonnementen historie
          </h3>
          <DataTable
            :value="historicalSubscriptions"
            size="small"
            data-key="teamleaderSubscriptionId"
            responsive-layout="scroll"
          >
            <template #empty>
              <div class="py-3 text-sm text-surface-500">
                Geen historische of toekomstige abonnementen.
              </div>
            </template>
            <Column field="title" header="Abonnement" />
            <Column header="Status">
              <template #body="{ data }">
                <Tag :severity="statusSeverity(data.status)" class="!text-xs !font-medium">
                  {{ statusLabel(data.status) }}
                </Tag>
              </template>
            </Column>
            <Column header="Start">
              <template #body="{ data }">
                <span class="text-sm font-mono">{{ formatDate(data.startsOn) }}</span>
              </template>
            </Column>
            <Column header="Einde">
              <template #body="{ data }">
                <span class="text-sm font-mono">{{ formatDate(data.endsOn) }}</span>
              </template>
            </Column>
            <Column header="Vacatureslots">
              <template #body="{ data }">
                <span class="text-sm font-mono">{{ data.jobSlots ?? '—' }}</span>
              </template>
            </Column>
            <Column header="Duur">
              <template #body="{ data }">
                <span class="text-sm font-mono">{{ durationLabel(data) }}</span>
              </template>
            </Column>
            <Column header="Prijs / maand">
              <template #body="{ data }">
                <span class="text-sm font-mono">{{ formatCurrency(data.pricePerMonth) }}</span>
              </template>
            </Column>
            <Column header="Totaal excl. BTW">
              <template #body="{ data }">
                <span class="text-sm font-mono">{{ formatCurrency(data.totalTaxExclusive) }}</span>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <!-- Invoices + credit notes (2-column grid) -->
    <div class="grid grid-cols-1 gap-5 xl:grid-cols-2">
      <Card>
        <template #content>
          <div class="flex flex-col gap-3">
            <h3 class="text-lg font-semibold">
              Facturen
            </h3>
            <div v-if="invoicesPending" class="space-y-2">
              <Skeleton v-for="n in 5" :key="n" height="2.5rem" />
            </div>
            <DataTable
              v-else
              :value="invoices"
              size="small"
              data-key="id"
              responsive-layout="scroll"
              paginator
              :rows="10"
              :rows-per-page-options="[10, 25, 50]"
            >
              <template #empty>
                <div class="py-3 text-sm text-surface-500">
                  Geen facturen gevonden.
                </div>
              </template>
              <Column field="invoiceNumber" header="Factuurnummer">
                <template #body="{ data }">
                  <span
                    v-tooltip.top="'Open factuur'"
                    class="cursor-pointer underline"
                    @click="downloadInvoice(data.fileId)"
                  >
                    {{ data.invoiceNumber }}
                  </span>
                </template>
              </Column>
              <Column header="Datum">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ formatDate(data.invoiceDate) }}</span>
                </template>
              </Column>
              <Column header="Excl. BTW">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ formatCurrency(data.totalTaxExclusive) }}</span>
                </template>
              </Column>
              <Column header="Incl. BTW">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ formatCurrency(data.totalTaxInclusive) }}</span>
                </template>
              </Column>
              <Column header="Openstaand">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ formatCurrency(data.due) }}</span>
                </template>
              </Column>
              <Column header="Credit-nummer">
                <template #body="{ data }">
                  <template v-if="findCreditNote(data.id) as ListCreditNotesForCompanyCreditNoteDto | undefined">
                    <span
                      v-tooltip.top="'Open credit-nota'"
                      class="cursor-pointer text-sm font-mono underline"
                      @click="downloadCreditNote(findCreditNote(data.id)!.id)"
                    >
                      {{ findCreditNote(data.id)!.creditNoteNumber ?? '—' }}
                    </span>
                  </template>
                  <span v-else class="text-sm font-mono">—</span>
                </template>
              </Column>
              <Column header="Status">
                <template #body="{ data }">
                  <Tag :severity="invoiceStatusSeverity(data)" class="!text-xs !font-medium">
                    {{ invoiceStatusLabel(data) }}
                  </Tag>
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </Card>

      <Card>
        <template #content>
          <div class="flex flex-col gap-3">
            <h3 class="text-lg font-semibold">
              Credit-nota's
            </h3>
            <div v-if="creditNotesPending" class="space-y-2">
              <Skeleton v-for="n in 5" :key="n" height="2.5rem" />
            </div>
            <DataTable
              v-else
              :value="creditNotes"
              size="small"
              data-key="id"
              responsive-layout="scroll"
              paginator
              :rows="10"
              :rows-per-page-options="[10, 25, 50]"
            >
              <template #empty>
                <div class="py-3 text-sm text-surface-500">
                  Geen credit-nota's gevonden.
                </div>
              </template>
              <Column field="creditNoteNumber" header="Credit-nummer">
                <template #body="{ data }">
                  <span
                    v-tooltip.top="'Open credit-nota'"
                    class="cursor-pointer underline"
                    @click="downloadCreditNote(data.id)"
                  >
                    {{ data.creditNoteNumber ?? '—' }}
                  </span>
                </template>
              </Column>
              <Column header="Datum">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ data.creditNoteDate ? formatDate(data.creditNoteDate) : '—' }}</span>
                </template>
              </Column>
              <Column header="Factuur">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ data.invoiceNumber ?? '—' }}</span>
                </template>
              </Column>
              <Column header="Excl. BTW">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ formatCurrency(data.totalTaxExclusive) }}</span>
                </template>
              </Column>
              <Column header="Incl. BTW">
                <template #body="{ data }">
                  <span class="text-sm font-mono">{{ formatCurrency(data.totalTaxInclusive) }}</span>
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>
